import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaClock, FaTag, FaShare, FaFacebook,
  FaTwitter, FaLinkedin, FaLink, FaThumbsUp, FaComment
} from 'react-icons/fa';
import SEOHelmet from '../SEOHelmet';
import Navbar from './Navbar';
import { buildBlogPath } from '../../utils/slugify';
import '../../styles/landing-page/BlogDetailPage.css';

const BlogDetailPage = () => {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(false);
  const [showCopied, setShowCopied] = useState(false);

  const blogIdentifier = id || slug || '';

  const fetchRelatedBlogs = useCallback(async (category) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/all?limit=3`
      );
      if (response.data.success) {
        const filtered = response.data.data
          .filter(b => b.category === category && b._id !== blogIdentifier)
          .slice(0, 3);
        setRelatedBlogs(filtered);
      }
    } catch (err) {
      console.error('Error fetching related blogs:', err);
    }
  }, [blogIdentifier]);

  const fetchBlog = useCallback(async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(
        `${API_BASE_URL}/api/blogs/${blogIdentifier}`
      );
      if (response.data.success) {
        setBlog(response.data.data);
        setComments(response.data.data.comments || []);
        fetchRelatedBlogs(response.data.data.category);
        setError('');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to load blog. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [blogIdentifier, fetchRelatedBlogs]);

  useEffect(() => {
    if (blogIdentifier) {
      fetchBlog();
    }
  }, [blogIdentifier, fetchBlog]);

  const stripHtml = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const calculateReadTime = (content) => {
    const text = stripHtml(content);
    const wordsPerMinute = 200;
    const words = text.trim().split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  const generateTableOfContents = (content) => {
    const temp = document.createElement('div');
    temp.innerHTML = content;
    const headings = Array.from(temp.querySelectorAll('h1, h2, h3'));
    return headings.map((heading, index) => ({
      id: `heading-${index}`,
      level: parseInt(heading.tagName[1]),
      text: heading.textContent
    }));
  };

  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const title = blog?.title || 'Check out this blog post';
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };
    if (links[platform]) {
      window.open(links[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    try {
      const newComment = {
        text: comment,
        author: 'Guest Reader',
        createdAt: new Date()
      };
      setComments([...comments, newComment]);
      setComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  const handleLike = () => {
    setLiked(!liked);
  };

  if (loading) {
    return (
      <div className="blog-detail-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-detail-page">
        <div className="error-container">
          <h2>Oops! Something went wrong</h2>
          <p>{error || 'Blog not found'}</p>
          <button onClick={() => navigate('/blogs')} className="btn-back">
            <FaArrowLeft /> Back to Blogs
          </button>
        </div>
      </div>
    );
  }

  const tableOfContents = generateTableOfContents(blog.content);
  const readTime = calculateReadTime(blog.content);

  return (
    <div className="blog-detail-page">
      <SEOHelmet
        page="blogDetail"
        replacements={{
          blogTitle: blog?.title || 'Asset Management Blog',
          blogExcerpt: blog?.description || 'Read expert insights on asset management and maintenance.',
        }}
        canonicalUrl={`https://www.assetflows.in/blog/${slug || ''}${slug && id ? '/' : ''}${id || ''}`}
      />
      {/* Header */}
      <header className="blog-detail-header">
        <Navbar />
        <button onClick={() => navigate('/blogs')} className="btn-back-nav">
          <FaArrowLeft /> Back to Blogs
        </button>
      </header>

      <main className="blog-detail-main">
        <div className="blog-detail-container">
          {/* Hero Section */}
          <div className="blog-detail-hero">
            <img
              src={blog.featuredImage || 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=1200&q=80'}
              alt={blog.title}
              className="hero-image"
            />
            <div className="hero-overlay"></div>
          </div>

          <div className="blog-detail-content-wrapper">
            {/* Main Content - Left */}
            <article className="blog-detail-article">
              {/* Article Header */}
              <header className="article-header">
                <div className="article-meta">
                  <span className="category-tag">{blog.category}</span>
                  <span className="read-time">
                    <FaClock /> {readTime} min read
                  </span>
                </div>

                <h1 className="article-title">{blog.title}</h1>

                <p className="article-excerpt">{blog.description}</p>

                {/* Author & Meta Info */}
                <div className="article-info-bar">
                  <div className="author-info">
                    <div className="author-avatar">
                      {blog.author?.name?.split(' ').map(n => n[0]).join('') || 'A'}
                    </div>
                    <div>
                      <div className="author-name">{blog.author?.name || 'Anonymous'}</div>
                      <div className="publish-date">
                        {new Date(blog.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Share & Engagement */}
                  <div className="article-actions">
                    <button
                      onClick={handleLike}
                      className={`action-btn ${liked ? 'active' : ''}`}
                      title="Like this article"
                    >
                      <FaThumbsUp /> {blog.likes?.length || 0}
                    </button>
                    <button className="action-btn" title="Comments">
                      <FaComment /> {comments.length}
                    </button>
                    <div className="share-menu">
                      <button className="action-btn share-btn" title="Share">
                        <FaShare /> Share
                      </button>
                      <div className="share-dropdown">
                        <button onClick={() => shareOnSocial('facebook')} className="share-option">
                          <FaFacebook /> Facebook
                        </button>
                        <button onClick={() => shareOnSocial('twitter')} className="share-option">
                          <FaTwitter /> Twitter
                        </button>
                        <button onClick={() => shareOnSocial('linkedin')} className="share-option">
                          <FaLinkedin /> LinkedIn
                        </button>
                        <button onClick={copyLink} className="share-option">
                          <FaLink /> Copy Link
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </header>

              {/* Article Content */}
              <div className="article-body">
                <div
                  className="rich-content"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                ></div>
              </div>

              {/* Social Share Section */}
              <div className="article-share-section">
                <span className="share-label">Share:</span>
                <div className="social-share-buttons">
                  <button onClick={() => shareOnSocial('facebook')} className="social-btn fb-btn" title="Share on Facebook">
                    <FaFacebook />
                  </button>
                  <button onClick={() => shareOnSocial('twitter')} className="social-btn twitter-btn" title="Share on Twitter">
                    <FaTwitter />
                  </button>
                  <button onClick={() => shareOnSocial('linkedin')} className="social-btn linkedin-btn" title="Share on LinkedIn">
                    <FaLinkedin />
                  </button>
                  <button onClick={copyLink} className="social-btn link-btn" title="Copy Link">
                    <FaLink />
                  </button>
                </div>
              </div>

              {/* Author Bio Section */}
              <div className="author-section">
                <div className="author-section-avatar">
                  {blog?.author?.name?.split(' ').map(n => n[0]).join('') || 'A'}
                </div>
                <div className="author-section-info">
                  <h4 className="author-section-name">{blog?.author?.name || 'Author'}</h4>
                  <p className="author-section-bio">
                    {blog?.author?.bio || 'Hello, We\'re content makers who are fascinated by technology, engineering, and innovation.'}
                  </p>
                </div>
              </div>

              {/* Navigation Section */}
              <div className="article-navigation">
                <div className="nav-item">
                  <div className="nav-label">Previous Post</div>
                  <button className="nav-link">5 Strategies to Scale Your API Infrastructure</button>
                </div>
                <div className="nav-item">
                  <div className="nav-label">Next Post</div>
                  <button className="nav-link">The Future of Real-Time Analytics</button>
                </div>
              </div>

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="article-tags">
                  <h4>Tags:</h4>
                  <div className="tags-list">
                    {blog.tags.map((tag, index) => (
                      <span key={index} className="tag-badge">
                        <FaTag size={12} /> {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments Section */}
              <section className="comments-section">
                <h2 className="comments-title">Comments ({comments.length})</h2>

                {/* Add Comment */}
                <div className="add-comment-form">
                  <h3>Leave a Comment</h3>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Share your thoughts..."
                    className="comment-input"
                    rows="4"
                  ></textarea>
                  <button
                    onClick={handleAddComment}
                    disabled={!comment.trim()}
                    className="btn-submit-comment"
                  >
                    <FaShare /> Post Comment
                  </button>
                </div>

                {/* Comments List */}
                <div className="comments-list">
                  {comments.length > 0 ? (
                    comments.map((cmt, index) => (
                      <div key={index} className="comment-item">
                        <div className="comment-avatar">
                          {(cmt.author || 'A')[0].toUpperCase()}
                        </div>
                        <div className="comment-content">
                          <div className="comment-author">{cmt.author}</div>
                          <div className="comment-date">
                            {new Date(cmt.createdAt).toLocaleDateString()}
                          </div>
                          <p className="comment-text">{cmt.text || cmt.content}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-comments">No comments yet. Be the first to comment!</p>
                  )}
                </div>
              </section>
            </article>

            {/* Sidebar - Table of Contents & Widgets - Right */}
            <aside className="blog-toc-sidebar">
              {/* Table of Contents */}
              {tableOfContents.length > 0 && (
                <div className="toc-container">
                  <h4 className="toc-title">Table of Contents</h4>
                  <nav className="toc-nav">
                    {tableOfContents.map((heading) => (
                      <a
                        key={heading.id}
                        href={`#${heading.id}`}
                        className={`toc-link toc-level-${heading.level}`}
                      >
                        {heading.text}
                      </a>
                    ))}
                  </nav>
                </div>
              )}

              {/* About the Blog */}
              <div className="about-author">
                <h3>About the Blog</h3>
                <div className="author-bio-avatar">
                  {blog?.author?.name?.split(' ')[0]?.[0]?.toUpperCase() || 'A'}
                </div>
                <div className="author-bio-name">{blog?.author?.name || 'Author'}</div>
                <p className="author-bio-text">
                  {blog?.author?.bio || 'Hello, We\'re content makers who are fascinated by technology, engineering, and innovation.'}
                </p>
                <div className="author-social-links" style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  {/* Add social links as needed */}
                </div>
              </div>

              {/* Popular Posts */}
              {relatedBlogs.length > 0 && (
                <div className="popular-posts-widget">
                  <h3>Popular Posts</h3>
                  {relatedBlogs.slice(0, 4).map((post) => (
                    <div key={post._id} className="popular-post-item">
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <img
                          src={post.featuredImage || 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=100&q=80'}
                          alt={post.title}
                          style={{ width: '50px', height: '50px', borderRadius: '4px', objectFit: 'cover' }}
                        />
                        <div style={{ flex: 1 }}>
                          <a href={buildBlogPath(post)} className="popular-post-title">
                            {post.title?.substring(0, 40)}...
                          </a>
                          <div className="popular-post-date">
                            {new Date(post.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Categories */}
              <div className="categories-widget">
                <h3>Categories</h3>
                <div className="category-item">
                  <input type="checkbox" id="cat-engineering" defaultChecked />
                  <label htmlFor="cat-engineering" className="category-label">
                    Engineering
                    <span className="category-count">24</span>
                  </label>
                </div>
                <div className="category-item">
                  <input type="checkbox" id="cat-ai" />
                  <label htmlFor="cat-ai" className="category-label">
                    AI & Automation
                    <span className="category-count">18</span>
                  </label>
                </div>
                <div className="category-item">
                  <input type="checkbox" id="cat-analytics" />
                  <label htmlFor="cat-analytics" className="category-label">
                    Analytics
                    <span className="category-count">12</span>
                  </label>
                </div>
                <div className="category-item">
                  <input type="checkbox" id="cat-security" />
                  <label htmlFor="cat-security" className="category-label">
                    Security
                    <span className="category-count">15</span>
                  </label>
                </div>
                <div className="category-item">
                  <input type="checkbox" id="cat-infrastructure" />
                  <label htmlFor="cat-infrastructure" className="category-label">
                    Infrastructure
                    <span className="category-count">6</span>
                  </label>
                </div>
              </div>

              {/* Newsletter Subscribe */}
              <div className="newsletter-banner">
                <h3>Subscribe</h3>
                <p>Get the latest posts delivered to your inbox.</p>
                <input type="email" placeholder="Your email" className="newsletter-input" />
                <button className="btn-submit-comment">Sign Up</button>
              </div>

              {/* Tags Widget */}
              {blog?.tags && blog?.tags.length > 0 && (
                <div className="tags-widget">
                  <h3>Tags</h3>
                  <div className="tags-list-widget">
                    {blog.tags.map((tag, index) => (
                      <a href={`/blog?tag=${tag}`} key={index} className="tag-link">
                        {tag}
                      </a>
                    ))}
                    {['React', 'TypeScript', 'DevOps', 'AWS', 'API', 'Cloud', 'Performance'].map((tag) => (
                      <a href={`/blog?tag=${tag}`} key={tag} className="tag-link">
                        {tag}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>

          {/* Related Posts */}
          {relatedBlogs.length > 0 && (
            <section className="related-posts-section">
              <h2 className="related-title">Related Articles</h2>
              <div className="related-posts-grid">
                {relatedBlogs.map((relatedBlog) => (
                  <article key={relatedBlog._id} className="related-post-card">
                    <div className="related-post-image">
                      <img
                        src={relatedBlog.featuredImage || 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&q=80'}
                        alt={relatedBlog.title}
                      />
                      <span className="related-category">{relatedBlog.category}</span>
                    </div>
                    <div className="related-post-content">
                      <h3>{relatedBlog.title}</h3>
                      <p>{relatedBlog.description?.substring(0, 100)}...</p>
                      <button
                        onClick={() => navigate(buildBlogPath(relatedBlog))}
                        className="btn-read-more"
                      >
                        Read More
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Copy Notification */}
      {showCopied && <div className="copy-notification">Link copied to clipboard!</div>}
    </div>
  );
};

export default BlogDetailPage;
